import { Button, HookedForm, InputField } from '@app/components';
import { Link } from 'react-router-dom';
import { useLoginPage } from './Login.hooks';
import Logo from '@app/assets/vertical-logo.svg?react';
import GoogleLogo from '@app/assets/icons/Google.svg?react';
import { useState, useEffect } from 'react';

export const Login = () => {
  const { loginForm, submitLogin, loginError, googleLoginFn, isLoading } =
    useLoginPage();
  const [rememberMe, setRememberMe] = useState(false);

  // Load saved email from localStorage on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      loginForm.setValue('email', savedEmail);
      setRememberMe(true);
    }
  }, [loginForm]);

  // Handle remember me checkbox change
  const handleRememberMeChange = (checked: boolean) => {
    setRememberMe(checked);
    if (!checked) {
      localStorage.removeItem('rememberedEmail');
    }
  };

  // Handle form submission to save email if remember me is checked
  const handleSubmit = (data: any) => {
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', data.email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }
    submitLogin({ ...data, rememberMe });
  };

  return (
    <div className="flex items-center justify-center flex-col min-h-screen py-6 bg-[#F5F8FA]">
      <Logo width={139} height={110} className="mb-8" />
      <div className="bg-white text-center space-y-6 rounded-lg max-w-[510px] px-10 pt-10 pb-6 shadow-sm">
        <div className="space-y-2">
          <h2 className="font-bold text-2xl text-[#1F2937]">Welcome back!</h2>
          <p className="text-sm text-[#6B7280]">
            Enter the information below to log in to your account
          </p>
        </div>
        
        {loginError && (
          <div className="bg-system-errorLight p-2 rounded-lg text-system-error font-medium">
            <p>{loginError}</p>
          </div>
        )}

        <HookedForm
          formHook={loginForm}
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          <div className="space-y-2 text-left">
            <label className="block text-sm font-medium text-[#374151]">
              Email
            </label>
            <InputField 
              name="email" 
              type="email"
              autoComplete="email"
              placeholder="e.g. user@example.com"
              className="w-full px-3 py-2 border border-[#D1D5DB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent"
            />
          </div>
          
          <div className="space-y-2 text-left">
            <div className="flex justify-between items-center">
              <label className="block text-sm font-medium text-[#374151]">
                Password
              </label>
              <Link to="/forgot-password" className="text-sm text-[#52B6FE] hover:underline">
                Forgot password?
              </Link>
            </div>
            <InputField 
              name="password" 
              type="password"
              autoComplete="current-password"
              placeholder="8+ characters"
              className="w-full px-3 py-2 border border-[#D1D5DB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent"
            />
          </div>
          
          <div className="flex items-center space-x-2 text-left">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => handleRememberMeChange(e.target.checked)}
              className="w-4 h-4 border border-[#D1D5DB] rounded focus:ring-2 focus:ring-[#3B82F6]"
            />
            <label htmlFor="rememberMe" className="text-sm text-[#374151]">
              Remember at this device
            </label>
          </div>
          
          <Button 
            type="submit" 
            className="w-full" 
            disabled={isLoading}
          >
            Log In
          </Button>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#9CA3AF]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-[#9CA3AF]">or</span>
            </div>
          </div>
          
          <button
            onClick={() => googleLoginFn()}
            type="button"
            className="w-full bg-white border border-[#D1D5DB] text-[#374151] font-medium py-2 px-4 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2 flex items-center justify-center space-x-2 disabled:opacity-50"
            disabled={isLoading}
          >
            <GoogleLogo className="w-5 h-5" />
            <span>Log in with Google</span>
          </button>
          <div className="p-4">
          <p className="text-xs text-[#9CA3AF]">
            By logging in, you agree to our{' '}
            <Link to="/terms-of-service" className="text-[#52B6FE] underline" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link to="/privacy-policy" className="text-[#52B6FE] underline" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </Link>
          </p>
          </div>
        </HookedForm>
      </div>
      <p className="mt-8 text-sm text-[#6B7280]">
        Don't have a business account?{' '}
        <Link to="/sign-up" className="text-[#52B6FE] hover:underline font-bold">
          Create one
        </Link>
      </p>
    </div>
  );
};
